import React, { useState, useEffect, useContext } from "react";
import {
    MantineReactTable, MRT_Icons, MRT_Row, MRT_RowSelectionState, MRT_ShowHideColumnsButton,
    MRT_ToggleFullScreenButton, useMantineReactTable, MRT_FilterFns
} from "mantine-react-table";
import { Group, Text, Tooltip } from "@mantine/core"
import { StackedBarCell } from "../Rankings/StackedBarCell";
import DataContext from "../../providers/DataProvider";
import HistogramChart from "./HistogramChart";
import { css } from '@emotion/css';
import UserSelectionContext from "../../providers/UserSelectionProvider";
import { MeshNode, scoreTypes } from "../../types";
import { BiSolidArrowFromBottom } from "react-icons/bi";
import { IoMdArrowDropdown, IoMdArrowDropright } from "react-icons/io";
import { LuDot } from "react-icons/lu";
import { getHardcodedColor, getHardcodedLabels } from "../../utils";
import { useDebounce } from "use-debounce";
import { FuzzyHighlighter } from "./FuzzyHighlighter"
import { fuzzyFilter } from "./customFilters";
import InfoCard from "../Info/InfoCard";
import { getInfo, infoType } from "../Info/infos";

const customIcons: Partial<MRT_Icons> = {
    IconFilter: (props: any) => (
        null
    ),
}

const MeshTableExpand: React.FC<{ row: MRT_Row<MeshNode>, searchTerm: string, handler: (e) => void }> = ({ row, searchTerm, handler }) => {
    const parents = row.getParentRows().map(r => r.original.name)

    return (
        <Group wrap="nowrap">
            {parents.map((parent) => (
                <Tooltip label={parent} >
                    <span style={{ display: 'inline-block' }}>
                        <BiSolidArrowFromBottom color="#ccc" style={{ cursor: 'help' }} />
                    </span>
                </Tooltip>
            ))}
            {row.getCanExpand() ?
                (row.getIsExpanded() ?
                    <IoMdArrowDropdown style={{ cursor: 'pointer' }} onClick={handler} />
                    : <IoMdArrowDropright style={{ cursor: 'pointer' }} onClick={handler} />)
                : <LuDot color="#ccc" />
            }
            <div style={{ maxWidth: 200 }}>
                <Tooltip label={row.getValue('name')} >
                    <Text size="xs" truncate="end">{FuzzyHighlighter(row.getValue('name'), searchTerm)}</Text>
                </Tooltip>
            </div>
        </Group>
    );
}

const MeshTable = () => {
    const { selectedIndications, SetSelectedIndications, selectedWeights } = useContext(UserSelectionContext)
    const { meshTree, loadingMeshTree, scoresDomain, meshId2Name, meshName2Ids, histograms } = useContext(DataContext)
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
    const [prevRowIdSelection, setPrevRowIdSelection] = useState<Set<string>>(new Set([]));
    const [firstLoad, setFirstLoad] = useState<boolean>(true);
    const [globalFilter, setGlobalFilter] = useState("");
    const [expanded, setExpanded] = useState({});
    const [previousExpanded, setPreviousExpanded] = useState(null);
    const [debouncedFilter] = useDebounce(globalFilter, 500);
    // const [mscoreFilter, setMscoreFilter] = useState<[number, number]>([-100, 100]);

    useEffect(() => {
        if (firstLoad) return;

        const newRowIdSelection = new Set(Object.keys(rowSelection));
        let newSelection = { ...rowSelection };

        if (prevRowIdSelection.size > newRowIdSelection.size) {
            const deselectedNames = new Set(
                [...prevRowIdSelection]
                    .filter(i => !newRowIdSelection.has(i))
                    .map(i => meshId2Name[i])
            );

            newSelection = Object.fromEntries(
                Object.entries(newSelection).filter(([id]) => !deselectedNames.has(meshId2Name[id]))
            );
        }

        if (prevRowIdSelection.size < newRowIdSelection.size) {
            newSelection = Object.fromEntries(
                Object.entries(rowSelection).flatMap(([k, v]) =>
                    meshName2Ids[meshId2Name[k]].map(id => [id, v])
                )
            );
        }

        const isSameSelection = (a, b) => {
            const keysA = Object.keys(a);
            const keysB = Object.keys(b);
            return keysA.length === keysB.length && keysA.every(key => b[key] === a[key]);
        };

        if (!isSameSelection(rowSelection, newSelection)) {
            setPrevRowIdSelection(new Set(Object.keys(newSelection)));
            setRowSelection(newSelection);
        }

        const newIndications = new Set(Object.keys(newSelection).map(id => meshId2Name[id]));

        const isSameSet = (a, b) =>
            a.size === b.size && [...a].every((item) => b.has(item));

        if (!isSameSet(selectedIndications, newIndications)) {
            SetSelectedIndications(newIndications);
        }

    }, [rowSelection]);

    useEffect(() => {
        const oldIndications = new Set(Object.keys(rowSelection).map(id => meshId2Name[id]));

        const isSameSet = (a, b) =>
            a.size === b.size && [...a].every((item) => b.has(item));

        if (!isSameSet(oldIndications, selectedIndications)) {
            const newSelection = Object.fromEntries(
                Array.from(selectedIndications).flatMap(indication => meshName2Ids[indication].map(id => [id, true]))
            )
            setPrevRowIdSelection(new Set(Object.keys(newSelection)))
            setRowSelection(newSelection);
        }

        if (firstLoad) setFirstLoad(false)
    }, [selectedIndications]);

    const columns = [
        {
            accessorKey: "name",
            header: "",
            Cell: () => <></>,
            size: 1,
            enableSorting: false,
            enableColumnFilter: false,
            enableColumnActions: false,
            filterVariant: 'text'
        },
        {
            accessorKey: scoreTypes.mscore,
            header: getHardcodedLabels(scoreTypes.mscore),
            // <Popover>
            //     <Popover.Target>
            //         <Text size="sm" fw={700}>{getHardcodedLabels(scoreTypes.mscore)}</Text>
            //     </Popover.Target>
            //     <Popover.Dropdown>
            //         <Stack gap='xs'>
            //             <HistogramChart
            //                 domain={[-100, 100]}
            //                 maxY={0}
            //                 addMargin={true}
            //                 removeZero={false}
            //                 gryOutZero={false}
            //                 showAxes={true}
            //                 c={getHardcodedColor(scoreTypes.mscore)}
            //                 data={histograms && selectedWeights ? histograms[scoreTypes.mscore][selectedWeights] : []}
            //             />
            //             {/* <RangeSlider style={{ width: 200 }} min={-100} max={100} value={mscoreFilter} onChangeEnd={setMscoreFilter}></RangeSlider> */}
            //         </Stack>
            //     </Popover.Dropdown>
            // </Popover>,
            Cell: ({ cell }) => {
                if (cell.getValue() === null)
                    return <Text size="xs" c='dimmed' ta='left'>Not computed.</Text>
                if (cell.getValue() === 0)
                    return <Text size="xs" c='dimmed' ta='left'>0%</Text>
                return <StackedBarCell
                    style={{ maxWidth: 100 }}
                    data={[{ [scoreTypes.mscore]: cell.getValue() }]}
                    showValues={true}
                    domain={scoresDomain} />
            },
            enableSorting: false,
            enableColumnFilter: true,
            enableColumnActions: false,
            filterVariant: 'range-slider',
            showColumnFilters: false,
            enableGlobalFilter: false,
            filterFn: (row, id, filterValue) => MRT_FilterFns['between'](row, id, [filterValue[0] / 100, filterValue[1] / 100]),
            mantineFilterRangeSliderProps: {
                color: '#45c8a8',
                step: 1,
                min: -100,
                max: 100
            },
        },
        {
            accessorKey: "histData",
            header: <InfoCard {...getInfo(infoType.meshHist)}><Text size="sm" fw={700}>{`${getHardcodedLabels(scoreTypes.mscore)} Distribution over Children`}</Text></InfoCard>,
            Cell: ({ cell }) => {
                if (!cell.row.original.children.length)
                    return <Text size="xs" c='dimmed' ta='left'>No children.</Text>
                if (!cell.getValue() || !cell.getValue().reduce((acc, c) => acc + c, 0))
                    return <Text size="xs" c='dimmed' ta='left'>All not computed or zero.</Text>
                return <HistogramChart c={getHardcodedColor(scoreTypes.mscore)} data={cell.getValue()} />
            },
            enableSorting: false,
            enableColumnFilter: false,
            enableColumnActions: false
        }
    ]

    const table = useMantineReactTable({
        columns,
        data: meshTree,
        enableRowSelection: (row) => row.original.mscore !== null,
        enableSelectAll: false,
        enableColumnResizing: false,
        enableDensityToggle: false,
        enableSubRowSelection: false,
        enableStickyHeader: true,
        enableGlobalFilter: true,
        enableExpanding: true,
        enableExpandAll: false,
        filterFromLeafRows: true,
        positionToolbarAlertBanner: 'none',
        getSubRows: (originalRow) => originalRow.children,
        getRowId: (originalRow) => originalRow.id,
        onRowSelectionChange: setRowSelection,
        onExpandedChange: setExpanded,
        state: {
            rowSelection, isLoading: meshTree.length == 0 || loadingMeshTree, globalFilter, expanded,
            // columnFilters: [{ id: scoreTypes.mscore, value: mscoreFilter }]
        },
        icons: customIcons,
        enableGlobalFilterModes: false,
        filterFns: {
            customFuzzy: (row, columnId, filterValue) => fuzzyFilter(row.getValue(columnId) ?? "", filterValue)
        },
        globalFilterFn: 'customFuzzy',
        globalFilter: globalFilter,
        onGlobalFilterChange: setGlobalFilter,
        positionGlobalFilter: "left",
        mantineSearchTextInputProps: {
            placeholder: 'Search indications',
            variant: 'filled',
        },
        initialState: {
            showColumnFilters: true,
            showGlobalFilter: true,
            density: 'xs',
            pagination: {
                pageIndex: 0,
                pageSize: 30
            },
            columnOrder: [
                'mrt-row-select',
                'mrt-row-expand',
                'mscore',
                'histData',
                'name',
            ]
        },
        mantinePaperProps: {
            withBorder: false,
            shadow: 'initial',
            style: {
                flex: 1,
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            },
            // Make the table cells smaller
            className: css`
              .mantine-Table-td {
                padding: 0px !important;
                padding-top: 0px !important;
                padding-bottom: 0px !important;
              }
            `
        },
        mantineTableBodyRowProps: ({ row }) => ({
            onClick: row.getToggleSelectedHandler(),
            style: { borderBottom: "block", cursor: 'pointer' },
        }),
        mantineSelectCheckboxProps: {
            size: 'xs'
        },
        displayColumnDefOptions: {
            'mrt-row-select': {
                size: 5,
                mantineTableHeadCellProps: {
                    style: {
                        color: 'transparent',
                        padding: 0
                    }
                }
            },
            'mrt-row-expand': {
                Cell: ({ cell }) => <MeshTableExpand row={cell.row} searchTerm={globalFilter} handler={(e) => {e.stopPropagation(); cell.row.getToggleExpandedHandler()()}} />,
                header: "Indication"
            }
        },
        mantineTableHeadCellProps: ({ column }) => {
            return {
                // align: 'center',
                // className: css`
                // .mrt-table-head-cell-labels {
                //     padding-left: 0px !important;
                //     padding-right: 0px !important;
                //     padding-top: 0px !important;
                //     padding-bottom: 0px !important;
                // }
                // `,
                style: {
                    paddingLeft: 0
                },
            };
        },
        renderToolbarInternalActions: ({ table }) => (
            <>
                <MRT_ShowHideColumnsButton table={table} />
                <MRT_ToggleFullScreenButton table={table} />
            </>
        )
    })

    useEffect(() => {
        if (globalFilter) {
            if (previousExpanded === null)
                setPreviousExpanded((prev) => prev ?? expanded);

            const getAllRowIds = (rows) => {
                return rows.flatMap((row) => [row.id, ...(row.subRows ? getAllRowIds(row.subRows) : [])]);
            };

            const filteredRowIds = getAllRowIds(table.getFilteredRowModel().rows);

            const newExpandedState = Object.fromEntries(filteredRowIds.map((id) => [id, true]));

            setExpanded(newExpandedState);
        } else if (previousExpanded !== null) {
            setExpanded(previousExpanded);
            setPreviousExpanded(null);
        }
    }, [debouncedFilter]);

    return (
        <MantineReactTable table={table} />
    );
}

export default MeshTable;
